import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Copy, ShareNetwork, Wallet, ArrowUp, LinkSimple, UserPlus, CurrencyInr, Lock } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  professional: { label: 'Professionals', tagline: 'Earn thousands/day', color: 'bg-indigo-600', badge: 'bg-indigo-100 text-indigo-700' },
  vendor:       { label: 'Vendors',       tagline: 'Earn in lakhs',      color: 'bg-rose-600',    badge: 'bg-rose-100 text-rose-700' },
  vendor_venue: { label: 'Venues',        tagline: 'Earn in lakhs',      color: 'bg-amber-600',   badge: 'bg-amber-100 text-amber-700' },
  customer:     { label: 'Customers',     tagline: 'Big one-time bonus', color: 'bg-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
};

const InviteAndEarn = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  // Admin-controlled visibility. `null` = still checking, `false` = hide.
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Resolve the admin gate first — only fetch referral info if enabled,
    // otherwise we return null below and skip the network entirely.
    axios.get(`${BACKEND_URL}/api/me/invite-earn-enabled`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const isOn = res.data?.enabled !== false;
        setEnabled(isOn);
        if (isOn) {
          fetchInfo();
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        // Fail-open: if the gate endpoint errors, behave as if enabled (pre-existing UX).
        if (cancelled) return;
        setEnabled(true);
        fetchInfo();
      });
    return () => { cancelled = true; };
  }, []);

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/referral/info`, { withCredentials: true });
      setData(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const inviteLink = data?.referral_code ? `${window.location.origin}/register?ref=${data.referral_code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied!');
  };

  const shareWhatsApp = () => {
    const text = `Join BeeWeddy — India's instant pricing event platform. Use my invite link and we both earn: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleWithdraw = async () => {
    const amt = parseInt(withdrawAmount, 10);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/referral/withdraw`, { amount: amt }, { withCredentials: true });
      toast.success(`Withdrawal of ₹${amt} requested!`);
      setWithdrawAmount('');
      fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    }
  };

  if (enabled === false) return null;
  if (loading) return <div className="text-center py-8 text-text-secondary">Loading earnings info...</div>;
  if (!data) return null;

  const { stats, wallet, referrals, by_tier: byTier = {}, can_withdraw: canWithdraw, withdrawal_min_invites: minInvites, settings } = data;
  const bonusAmount = settings?.invite_bonus_amount ?? 50;
  const clawbackDays = settings?.invite_bonus_clawback_days ?? 90;

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <Card className="bg-gradient-to-r from-brand-primary/10 to-amber-50 border-brand-primary/30" data-testid="invite-earn-hero">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <CurrencyInr size={32} className="text-brand-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-heading text-xl font-bold text-text-primary">Invite & Earn Program</p>
              <p className="text-sm text-text-secondary mt-1">
                Earn <strong>₹{bonusAmount} instantly</strong> for every successful signup, plus commission on their bookings.
                Bonus is reversed if invitee doesn't book within {clawbackDays} days.
              </p>
              <p className="text-xs text-text-secondary mt-2">
                <Lock size={12} weight="bold" className="inline mr-1" />
                Wallet withdrawal unlocks after <strong>{minInvites} invites</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission promise note */}
      <p className="text-sm font-bold text-text-primary" data-testid="commission-on-booking-note">
        {t('pro.commission_on_every_booking')}
      </p>

      {/* Per-tier streak cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tier-grid">
        {Object.entries(byTier).map(([key, tier]) => {
          const meta = TIER_META[key] || { label: key, tagline: '', color: 'bg-gray-600', badge: 'bg-gray-100 text-gray-700' };
          const pct = tier.required_invites > 0
            ? Math.min(100, Math.round((tier.invites_in_period / tier.required_invites) * 100))
            : 100;
          const hasStreak = tier.required_invites > 0 && tier.period_days > 0;
          return (
            <Card key={key} className={`border-2 ${tier.target_met ? 'border-green-400' : 'border-[#E8E1D5]'}`} data-testid={`tier-card-${key}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] px-2 py-0.5 text-white font-bold uppercase tracking-wider ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span className="text-xs font-bold text-brand-primary">{tier.commission_percentage}%</span>
                </div>
                <div className="text-xs text-text-secondary mb-3">{meta.tagline}</div>
                {hasStreak ? (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                      <div className={`h-2 rounded-full transition-all ${tier.target_met ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-text-secondary">
                      <span data-testid={`tier-progress-${key}`}>{tier.invites_in_period} / {tier.required_invites} in {tier.period_days}d</span>
                      <span className={`font-bold uppercase ${tier.target_met ? 'text-green-700' : 'text-amber-700'}`}>
                        {tier.target_met ? 'On streak' : 'Streak pending'}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] text-emerald-700 font-bold uppercase">No streak — always earning</div>
                )}
                <div className="mt-3 pt-2 border-t border-[#E8E1D5] flex items-center justify-between text-xs">
                  <span className="text-text-secondary">Total invited</span>
                  <span className="font-bold" data-testid={`tier-total-${key}`}>{tier.total_invited}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">Booked</span>
                  <span className="font-bold">{tier.booked}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Invite Link Card */}
      <Card className="bg-white border-[#E8E1D5]" data-testid="invite-link-card">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <LinkSimple size={24} className="text-brand-primary" /> Your Invite Link
          </CardTitle>
          <CardDescription>
            One link — invite professionals, vendors, or customers. Each invite earns ₹{bonusAmount} instantly + commission on their bookings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Input value={inviteLink} readOnly className="rounded-none border-[#E8E1D5] flex-1 text-sm font-mono bg-gray-50" data-testid="invite-link-input" />
            <Button onClick={copyLink} variant="outline" className="rounded-none border-[#E8E1D5]" data-testid="copy-invite-btn">
              <Copy size={16} className="mr-1" /> Copy
            </Button>
            <Button onClick={shareWhatsApp} className="rounded-none bg-green-600 hover:bg-green-700 text-white" data-testid="share-whatsapp-btn">
              <ShareNetwork size={16} className="mr-1" /> WhatsApp
            </Button>
          </div>
          <p className="text-xs text-text-secondary mt-2">
            Your code: <span className="font-bold text-brand-primary" data-testid="referral-code-display">{data.referral_code}</span>
          </p>
        </CardContent>
      </Card>

      {/* Stats + Wallet */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border-[#E8E1D5]" data-testid="referral-stats-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <UserPlus size={20} className="text-brand-primary" /> Referral Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center border border-[#E8E1D5] rounded p-3">
                <div className="font-heading text-2xl font-bold text-brand-primary" data-testid="stat-invited">{stats?.total_invited || 0}</div>
                <div className="text-[10px] text-text-secondary uppercase tracking-wider">Invited</div>
              </div>
              <div className="text-center border border-[#E8E1D5] rounded p-3">
                <div className="font-heading text-2xl font-bold text-amber-600" data-testid="stat-commission">
                  ₹{(stats?.total_commission || 0).toLocaleString('en-IN')}
                </div>
                <div className="text-[10px] text-text-secondary uppercase tracking-wider">Commission earned</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E8E1D5]" data-testid="wallet-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Wallet size={20} className="text-brand-primary" /> Wallet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-4 p-4 bg-brand-primary/5 border border-brand-primary/20 rounded">
              <div className="text-xs text-text-secondary uppercase tracking-wider mb-1">Available Balance</div>
              <div className="font-heading text-3xl font-bold text-brand-primary" data-testid="wallet-balance">
                ₹{(wallet?.balance || 0).toLocaleString('en-IN')}
              </div>
            </div>
            {!canWithdraw && (
              <div className="mb-3 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800" data-testid="withdraw-gate-notice">
                <Lock size={12} weight="bold" className="inline mr-1" />
                Invite {minInvites - (stats?.total_invited || 0)} more {minInvites - (stats?.total_invited || 0) === 1 ? 'person' : 'people'} to unlock withdrawal.
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input type="number" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Amount (₹)" className="rounded-none border-[#E8E1D5] flex-1 h-9" data-testid="withdraw-amount-input"
                disabled={!canWithdraw} />
              <Button onClick={handleWithdraw} className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50" data-testid="withdraw-btn"
                disabled={!canWithdraw || !withdrawAmount || parseInt(withdrawAmount, 10) <= 0 || parseInt(withdrawAmount, 10) > (wallet?.balance || 0)}>
                <ArrowUp size={14} className="mr-1" /> Withdraw
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral List */}
      {referrals && referrals.length > 0 && (
        <Card className="bg-white border-[#E8E1D5]" data-testid="referrals-list-card">
          <CardHeader><CardTitle className="font-heading text-xl">Your Invitees</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {referrals.map((ref, idx) => {
                const tierKey = ref.invitee_role === 'vendor' && (ref.invitee_category || '').toLowerCase() === 'venue'
                  ? 'vendor_venue' : ref.invitee_role;
                const meta = TIER_META[tierKey] || { label: ref.invitee_role || 'unknown', badge: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-[#E8E1D5] rounded" data-testid={`ref-row-${idx}`}>
                    <div className="min-w-0">
                      <span className="font-medium text-sm truncate">{ref.referred_name}</span>
                      <span className="text-xs text-text-secondary ml-2">{ref.referred_phone}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${meta.badge}`}>{meta.label}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                        ref.status === 'booked' ? 'bg-green-100 text-green-700' :
                        ref.status === 'registered' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>{ref.status}</span>
                      {ref.bonus_clawed_back ? (
                        <span className="text-xs text-red-600 font-bold" title={`No booking in ${clawbackDays}d`}>−₹{ref.bonus_amount || bonusAmount}</span>
                      ) : ref.reward_credited ? (
                        <span className="text-xs text-green-600 font-bold">+₹{ref.bonus_amount || bonusAmount}</span>
                      ) : null}
                      {ref.commission_earned > 0 && (
                        <span className="text-xs text-brand-primary font-bold">+₹{ref.commission_earned}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InviteAndEarn;
